import React, {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import {
    Box,
    EmptyState,
    Flex,
    Grid,
    GridProps,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    BlockHeader,
    Button,
    CatalogSection,
    ICatalogSelectedValue,
    useAppDispath,
} from 'shared';
// eslint-disable-next-line no-restricted-imports
import { TabsItemProps } from 'shared/ui/Tabs';
import {
    DeliveryArticleCard,
    DeliveryArticleCardModel,
} from 'entities/delivery-article';
import { useSearchParams } from 'react-router-dom';
import { LeadSmallSection } from 'features';
import { DeliveryArticleTagModel } from 'entities/delivery-article-tag';
import { DeliveryArticleCategoryModel } from 'entities/delivery-article-category';
import { FaSearch as CiSearch2 } from 'react-icons/fa';
import { useAppDataProcessState } from 'processes/load-app-data/model/slice';
// eslint-disable-next-line no-restricted-imports
import { Modal } from 'shared/ui/Modal';
import { LuFilter } from 'react-icons/lu';
import { Helmet } from 'react-helmet-async';
// eslint-disable-next-line no-restricted-imports
import { richTextToString } from 'shared/lib/richtext-to-string';
import { DeliveryPageMainTabsContent } from './DeliveryPageMainTabsContent';
import { DeliveryPageActions, useDeliveryPageState } from '../../store';

// eslint-disable-next-line no-undef
const CiSearch = CiSearch2 as () => JSX.Element;

const createDeliveryPageMainTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem" transition="0.5s">{label.toLocaleUpperCase()}</Text>
);

const createDeliveryPageMainTabsContentItem = (
    key: string,
    deliveryArticleTags: DeliveryArticleTagModel[],
    deliveryArticlesCards: DeliveryArticleCardModel[],
    selectedTagUid: string,
    onChangeSelectedTag: (v: string) => void,
) => (
    <DeliveryPageMainTabsContent
        selectedTagUid={selectedTagUid}
        tags={deliveryArticleTags}
        cards={deliveryArticlesCards}
        onChangeTag={(v) => onChangeSelectedTag(v)}
        transition="0.5s"
    />
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createDeliveryPageMainTabsItems = (
    deliveryArticleCategories: DeliveryArticleCategoryModel[],
    deliveryArticleTags: DeliveryArticleTagModel[],
    deliveryArticlesCards: DeliveryArticleCardModel[],
    selectedTabUid: string,
    selectedTagUid: string,
    onChangeSelectedTag: (v: string) => void,
): TabsItemProps[] => deliveryArticleCategories.map(({
    uid,
    name,
}) => ({
    key: uid,
    labelComponent: createDeliveryPageMainTabsItemLabel(name),
    contentComponent: createDeliveryPageMainTabsContentItem(
        uid,
        deliveryArticleTags,
        deliveryArticlesCards,
        selectedTagUid,
        onChangeSelectedTag,
    ),
}));

export type DeliveryPageMainProps = GridProps & React.RefAttributes<HTMLDivElement>

export const DeliveryPageMain: React.FC<DeliveryPageMainProps> = ({
    ...props
}) => {
    const {
        deliveryArticleCategories,
        deliveryArticleTags,
        deliveryArticlesCards,
        title,
        image,
        description,
        leadFormPdf,
        total,
    } = useDeliveryPageState();

    const { defaultDeliveryFIlter } = useAppDataProcessState();
    const dispatch = useAppDispath();

    const [searchParams, setSearchParams] = useSearchParams();

    const ref = useRef<HTMLDivElement>(null);
    const headerReaf = useRef<HTMLDivElement>(null);

    const [catalogValue, setCatalogValue] = useState<ICatalogSelectedValue>({
        filter: null,
    });

    const [page, setPage] = useState(1);

    useLayoutEffect(() => {
        dispatch(DeliveryPageActions.getDeliveryService());
    }, []);

    useEffect(() => {
        const urlFilter = searchParams.get('filter');
        if (urlFilter) {
            setCatalogValue({
                filter: urlFilter,
            });
        } else if (defaultDeliveryFIlter) {
            setCatalogValue({
                filter: defaultDeliveryFIlter,
            });
        } else {
            setCatalogValue({
                filter: 'all',
            });
        }
    }, [searchParams.get('filter')]);

    useEffect(() => {
        if (catalogValue.filter !== null) {
            setSearchParams({
                filter: catalogValue.filter,
            });

            if (catalogValue.filter === 'all') {
                dispatch(DeliveryPageActions.getDeliveryArticlesByCategoryAndTag({
                    page: 1,
                }));
            } else {
                const [key, value] = catalogValue.filter.split('_');

                if (key === 'category') {
                    dispatch(DeliveryPageActions.getDeliveryArticlesByCategoryAndTag({
                        categoryUid: value,
                        page: 1,
                    }));
                }

                if (key === 'tag') {
                    dispatch(DeliveryPageActions.getDeliveryArticlesByCategoryAndTag({
                        tagUid: value,
                        page: 1,
                    }));
                }
            }
            setPage(1);
        }
    }, [JSON.stringify(catalogValue)]);

    const handleLoadMoreClick = () => {
        if (catalogValue.filter === 'all') {
            dispatch(DeliveryPageActions.loadMoreDeliveryArticlesByCategoryAndTag({
                page: page + 1,
            }));
        } else {
            const [key, value] = catalogValue.filter.split('_');

            if (key === 'category') {
                dispatch(DeliveryPageActions.loadMoreDeliveryArticlesByCategoryAndTag({
                    categoryUid: value,
                    page: page + 1,
                }));
            }

            if (key === 'tag') {
                dispatch(DeliveryPageActions.loadMoreDeliveryArticlesByCategoryAndTag({
                    tagUid: value,
                    page: page + 1,
                }));
            }
        }
        setPage(page + 1);
    };

    const [open, setOpen] = useState(false);

    return (
        <Grid
            ref={ref}
            {...props}
        >
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={richTextToString(description)} />
                <meta property="og:title" content={title} />
            </Helmet>
            <Flex
                ref={headerReaf}
            >
                <BlockHeader
                    title={title}
                    imageSrc={image.url}
                    description={description}
                    width={{
                        base: '100vw',
                        sm: '100%',
                    }}
                />
            </Flex>
            <Grid
                background="#000000"
            >
                <LeadSmallSection
                    title="НЕ МОЖЕТЕ ОПРЕДЕЛИТЬСЯ?"
                    padding="2rem"
                    buttonText="Заказать звонок"
                    background="#000000"
                    maxWidth={{
                        base: '425px',
                        lg: '1280px',
                    }}
                    width="100%"
                    margin="0 auto"
                    pdfUrl={leadFormPdf?.url}
                />
            </Grid>
            <Grid
                maxWidth={{
                    base: '425px',
                    lg: '1280px',
                }}
                background="white"
                width="100%"
                margin="0 auto"
                padding="2rem 0"
                templateColumns={{
                    base: '1fr',
                    lg: '1fr 2fr',
                }}
            >
                <Box>
                    <CatalogSection
                        display={{
                            base: 'none',
                            lg: 'block',
                        }}
                        position="sticky"
                        top="2rem"
                        maxHeight="100vh"
                        overflow="scroll"
                        scrollbar="hidden"
                        value={catalogValue}
                        handleChange={setCatalogValue}
                        groups={[
                            {
                                key: 'filter',
                                value: '',
                                title: 'Категории',
                                items: deliveryArticleCategories.map(({ uid, name }) => ({
                                    key: `category_${uid}`,
                                    value: `category_${uid}`,
                                    title: name,
                                })),
                            },
                            {
                                key: 'filter',
                                value: '',
                                title: 'Поводы',
                                items: deliveryArticleTags.map(({ uid, name }) => ({
                                    key: `tag_${uid}`,
                                    value: `tag_${uid}`,
                                    title: name,
                                })),
                            },
                        ]}
                        background="#000000"
                        color="white"
                        padding="2rem"
                        height="fit-content"
                    />
                    <Button
                        display={{
                            base: 'block',
                            lg: 'none',
                        }}
                        position="fixed"
                        bottom="10rem"
                        left="0"
                        zIndex={10}
                        onClick={() => setOpen(true)}
                        fontWeight="bold"
                        fontSize="1.5rem"
                        height="fit-content"
                    >
                        <Grid templateColumns="auto 1fr" gap="1rem">
                            <LuFilter size="1.5rem" />
                            <Text margin="auto">
                                Фильтры
                            </Text>
                        </Grid>
                    </Button>
                </Box>
                {deliveryArticlesCards.length > 0 ? (
                    <Grid>
                        <Grid
                            templateColumns={{
                                base: '1fr 1fr',
                                lg: '1fr 1fr 1fr',
                            }}
                            height="fit-content"
                            gap="2rem"
                            padding={{
                                base: '2rem 2rem 0 2rem',
                                lg: '0 2rem',
                            }}
                        >
                            {deliveryArticlesCards.map(({
                                uid,
                                name,
                                weight,
                                price,
                                currency,
                                image,
                            }) => (
                                <DeliveryArticleCard
                                    uid={uid}
                                    name={name}
                                    weight={weight || undefined}
                                    price={price || undefined}
                                    currencyAbbreviation={currency?.abbreviation}
                                    image={(image && {
                                        src: image.url,
                                    }) || undefined}
                                    link={{
                                        to: `/delivery/${uid}`,
                                    }}
                                />
                            ))}
                        </Grid>
                        {total && (deliveryArticlesCards.length < total) ? (
                            <Grid paddingTop="2rem">
                                <Button
                                    background="#E74C3C"
                                    margin="auto"
                                    onClick={handleLoadMoreClick}
                                    size="md"
                                >
                                    Загрузить ещё
                                </Button>
                            </Grid>
                        ) : undefined}
                    </Grid>
                ) : (
                    <EmptyState.Root padding="2rem 0 0 0" size="lg">
                        <EmptyState.Content>
                            <EmptyState.Indicator>
                                <CiSearch />
                            </EmptyState.Indicator>
                            <VStack textAlign="center">
                                <EmptyState.Title>
                                    <Text color="#000000">
                                        Ничего не найдено
                                    </Text>
                                </EmptyState.Title>
                            </VStack>
                        </EmptyState.Content>
                    </EmptyState.Root>
                )}
            </Grid>
            <Modal
                title=""
                open={open}
                onChange={setOpen}
                innerProps={{
                    padding: 0,
                    gap: 0,
                    background: '#000000',
                }}
            >
                <Grid>
                    <CatalogSection
                        position="sticky"
                        top="2rem"
                        maxHeight="100vh"
                        overflow="scroll"
                        scrollbar="hidden"
                        value={catalogValue}
                        handleChange={(v) => {
                            setCatalogValue(v);
                            setTimeout(() => {
                                setOpen(false);
                            }, 500);
                        }}
                        groups={[
                            {
                                key: 'filter',
                                value: '',
                                title: 'Категории',
                                items: deliveryArticleCategories.map(({ uid, name }) => ({
                                    key: `category_${uid}`,
                                    value: `category_${uid}`,
                                    title: name,
                                })),
                            },
                            {
                                key: 'filter',
                                value: '',
                                title: 'Поводы',
                                items: deliveryArticleTags.map(({ uid, name }) => ({
                                    key: `tag_${uid}`,
                                    value: `tag_${uid}`,
                                    title: name,
                                })),
                            },
                        ]}
                        background="#000000"
                        color="white"
                        padding="2rem"
                        height="fit-content"
                    />
                </Grid>
                <Grid>
                    <Button
                        onClick={() => setOpen(false)}
                        background="#E74C3C"
                        fontWeight="bold"
                        fontSize="1.5rem"
                    >
                        Закрыть
                    </Button>
                </Grid>
            </Modal>
        </Grid>
    );
};
